import React, { useState } from 'react'
import styled from 'styled-components'
import {
    Box,
    Chip,
    Pagination,
    Paper,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { KEYS } from 'utils/intl/constant'
import { translate } from 'utils/intl/helper'
import { useAppSelector } from '../../../../store/hooks'

const StyledBox = styled(Box)`
    .MuiTableCell-root {
        color: #757575;
    }
`

const StyledBoxEnd = styled(Box)`
    display: flex;
    justify-content: flex-end;
    padding-top: 24px;
`

interface Data {
    size: string
    option: string
    status: string
}

type Order = 'asc' | 'desc'

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] > a[orderBy]) {
        return 1
    }
    return 0
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) {
            return order
        }
        return a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
}

interface EnhancedTableProps {
    numSelected: number
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
    order: Order
    orderBy: string
    rowCount: number
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const intl = useAppSelector((state) => state.ui.intl)
    const { order, orderBy, onRequestSort } = props
    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property)
    }

    interface HeadCell {
        disablePadding: boolean
        id: keyof Data
        label: string
        numeric: boolean
    }

    const headCells: readonly HeadCell[] = [
        {
            id: '',
            numeric: false,
            disablePadding: false,
            label: '',
        },
        {
            id: 'size',
            numeric: false,
            disablePadding: false,
            label: translate(intl, KEYS.UI_SIZE),
        },
        {
            id: 'option',
            numeric: false,
            disablePadding: false,
            label: translate(intl, KEYS.UI_OPTION),
        },
        {
            id: 'status',
            numeric: false,
            disablePadding: false,
            label: translate(intl, KEYS.UI_LASTUPDATE),
        },
    ]

    return (
        <TableHead>
            <TableRow>
                <TableCell padding='checkbox' />
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align='right'
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}>
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component='span' sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    )
}

interface CandidatesTableProps {
    rows: any[]
    page: number
    rowsPerPage: number
    totalRows: number
    selectedId: string
    onRowSelect: (id: string) => void
    onChangePage: (event: unknown, newPage: number) => void
}

const CandidatesTable: React.FC<CandidatesTableProps> = ({
    rows,
    page,
    rowsPerPage,
    totalRows,
    selectedId,
    onRowSelect,
    onChangePage,
}) => {
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<keyof Data>('size')

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const isSelected = (id: string) => selectedId === id

    return (
        <StyledBox>
            <Paper elevation={0}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            numSelected={selectedId.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={() => {}}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {rows?.length
                                ? stableSort(rows, getComparator(order, orderBy)).map((row: any, index: number) => {
                                      const isItemSelected = isSelected(row.id)
                                      const options = row.options.map((o: any) => ({
                                          key: o.Key,
                                          description: o.Description,
                                          shortDescription: o.ShortDescription,
                                      }))

                                      return (
                                          <TableRow
                                              hover
                                              onClick={(event) => {
                                                  event.preventDefault()
                                                  onRowSelect(row.id)
                                              }}
                                              role='checkbox'
                                              aria-checked={isItemSelected}
                                              tabIndex={-1}
                                              key={index}
                                              selected={isItemSelected}>
                                              <TableCell></TableCell>
                                              <TableCell padding='checkbox'>
                                                  <Radio color='primary' checked={isItemSelected} />
                                              </TableCell>
                                              <TableCell align='right'>{row.size}</TableCell>
                                              <TableCell align='right'>
                                                  <Stack
                                                      direction='row'
                                                      spacing={1}
                                                      flexWrap='wrap'
                                                      justifyContent='flex-end'>
                                                      {options.map((option, idx) => (
                                                          <Chip
                                                              key={idx}
                                                              label={option.shortDescription}
                                                              variant='outlined'
                                                              size='small'
                                                          />
                                                      ))}
                                                  </Stack>
                                              </TableCell>
                                              <TableCell align='right'>
                                                  {new Date(row.info.updated_at).toLocaleDateString()}
                                              </TableCell>
                                          </TableRow>
                                      )
                                  })
                                : Array.from({ length: rowsPerPage }).map((_, index) => (
                                      <TableRow key={index}>
                                          <TableCell colSpan={6} />
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <StyledBoxEnd>
                    <Pagination
                        page={page}
                        onChange={onChangePage}
                        count={Math.round(totalRows / rowsPerPage)}
                        variant='outlined'
                        color='primary'
                        shape='rounded'
                    />
                </StyledBoxEnd>
            </Paper>
        </StyledBox>
    )
}

export default CandidatesTable
